/**
 * Presentational modules for the RAPID v2 Intake redesign.
 * Mirrors the Metrics/Diagnosis view-switcher pattern: an always-on Intake Summary (which also hosts
 * the master Standard/Operator mode toggle so mode selection never needs a view change), a section nav
 * whose Expand-all/Collapse button is a sibling OUTSIDE the role=tablist (axe), and one view per
 * section rendered mounted-but-hidden. The owning IntakeTab keeps all state + handlers; the internal
 * mode value (`seeded`/`operator`) is unchanged — only rendered copy changed.
 */

export const INTAKE_SECTIONS = [
  { key: 'summary', label: 'Summary' },
  { key: 'operator', label: 'Operator Extraction' },
  { key: 'review', label: 'Extraction Review' },
  { key: 'rfis', label: 'RFIs & Stakeholders' },
  { key: 'transcript', label: 'Source Transcript' },
];

const REVIEW_STATUSES = ['Accepted', 'Needs edit', 'Rejected'];

function reviewSlug(status) {
  return String(status).toLowerCase().replace(/\s+/g, '-');
}

// ---------------------------------------------------------------------------
// Section nav (view-switcher)
// ---------------------------------------------------------------------------

export function IntakeSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="intake-section-nav" data-testid="intake-section-nav">
      <div className="intake-nav-tabs" role="tablist" aria-label="Intake sections">
        {INTAKE_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`intake-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`intake-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`intake-nav-chip intake-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="intake-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? 'summary' : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Always-on Intake Summary (hosts the mode toggle)
// ---------------------------------------------------------------------------

export function IntakeSummaryPanel({
  mode,
  acknowledged,
  extractionStatus,
  reviewCounts,
  rfiCount,
  stakeholderCount,
  onSetMode,
}) {
  const modeLabel = mode === 'operator' ? 'Operator mode' : 'Standard mode';
  return (
    <section className="rapid-panel intake-summary-panel" data-testid="intake-summary-panel">
      <div className="intake-summary-head">
        <div className="eyebrow">Intake</div>
        <h3 className="intake-summary-headline">
          Standard mode by default; operator mode available for approved safe text
        </h3>
      </div>

      <div className="rapid-toolbar intake-mode-toolbar">
        <button
          type="button"
          className={`btn ${mode === 'seeded' ? 'primary' : 'ghost'} sm`}
          onClick={() => onSetMode('seeded')}
        >
          Standard mode
        </button>
        <button
          type="button"
          className={`btn ${mode === 'operator' ? 'primary' : 'ghost'} sm`}
          onClick={() => onSetMode('operator')}
        >
          Operator mode
        </button>
        <span className="chip outline sm" data-testid="toggle-ai-mode">
          {modeLabel}
        </span>
      </div>

      <div className="intake-summary-stats">
        <div className="intake-summary-stat is-primary">
          <span className="k">Extraction status</span>
          <span className="v is-text" data-testid="intake-summary-status">
            {extractionStatus || 'Accepted'}
          </span>
          <span className="sub">Human-reviewed</span>
        </div>
        <div className="intake-summary-stat">
          <span className="k">Field review</span>
          <span className="v" data-testid="intake-summary-accepted">
            {reviewCounts.accepted}
          </span>
          <span className="sub">
            {reviewCounts.needsEdit} needs edit · {reviewCounts.rejected} rejected
          </span>
        </div>
        <div className="intake-summary-stat">
          <span className="k">RFIs</span>
          <span className="v" data-testid="intake-summary-rfi-count">
            {rfiCount}
          </span>
          <span className="sub">{stakeholderCount} stakeholders</span>
        </div>
        <div className="intake-summary-stat">
          <span className="k">Operator safety</span>
          <span className="v is-text" data-testid="intake-summary-ack">
            {acknowledged ? 'Acknowledged' : 'Not acknowledged'}
          </span>
          <span className="sub">No CUI/PHI/PII in operator mode</span>
        </div>
      </div>

      <div className="rapid-note">AI suggestion — requires human acceptance</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Summary "start here" view
// ---------------------------------------------------------------------------

export function IntakeSummaryView() {
  return (
    <section className="rapid-panel" data-testid="intake-summary-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Start here</div>
          <h3>How intake works</h3>
        </div>
      </div>
      <p>
        Standard mode uses the prepared intake record and requires no live AI. Operator mode is an
        experimental capability for approved, non-sensitive text only. Either way, the extracted
        fields are human-reviewed before they flow into the canvas.
      </p>
      <ul className="rapid-inline-list">
        <li>
          <strong>Operator Extraction</strong> — run extraction or test the fallback path on safe
          text.
        </li>
        <li>
          <strong>Extraction Review</strong> — accept, edit, or reject the title, problem, and North
          Star.
        </li>
        <li>
          <strong>RFIs &amp; Stakeholders</strong> — open questions and the people who must answer
          them.
        </li>
        <li>
          <strong>Source Transcript</strong> — the supporting intake transcript.
        </li>
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Operator Extraction
// ---------------------------------------------------------------------------

export function OperatorExtractionView({
  mode,
  acknowledged,
  liveFailure,
  customText,
  customEcho,
  onToggleAck,
  onRunExtraction,
  onTestFallback,
  onCustomTextChange,
  onRunCustom,
}) {
  const operatorReady = mode === 'operator' && acknowledged;
  return (
    <section className="rapid-panel" data-testid="intake-operator-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Operator extraction</div>
          <h3>Experimental — approved safe text only</h3>
        </div>
      </div>
      <p className="muted">
        Switch to operator mode in the summary above, acknowledge the safety constraint, then run
        extraction. The fallback path proves graceful degradation to the standard extraction.
      </p>
      <label className="rapid-checkbox">
        <input
          type="checkbox"
          data-testid="ack-no-sensitive-data"
          checked={acknowledged}
          onChange={(event) => onToggleAck(event.target.checked)}
        />
        No CUI, PHI, PII, EDIPI, SSN, or operationally sensitive text will be used in operator mode.
      </label>
      <div className="rapid-inline-actions">
        <button
          type="button"
          className="btn ghost sm"
          disabled={!operatorReady}
          onClick={onRunExtraction}
        >
          Run extraction
        </button>
        <button
          type="button"
          className="btn ghost sm"
          disabled={!operatorReady}
          onClick={onTestFallback}
        >
          Test fallback path
        </button>
      </div>
      {liveFailure ? (
        <div className="rapid-note warning">
          Extraction fallback completed. Standard extraction fields were restored for review.
        </div>
      ) : null}
      <label className="rapid-field">
        <span>Custom operator input</span>
        <textarea
          data-testid="custom-intake-text"
          value={customText}
          onChange={(event) => onCustomTextChange(event.target.value)}
          rows={4}
        />
      </label>
      <div className="rapid-inline-actions">
        <button
          type="button"
          className="btn ghost sm"
          data-testid="run-custom-intake"
          disabled={!acknowledged || !customText}
          onClick={onRunCustom}
        >
          Run custom extraction
        </button>
      </div>
      {customEcho ? (
        <div className="rapid-note" data-testid="custom-intake-echo">
          Custom operator input captured: {customEcho}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Extraction Review
// ---------------------------------------------------------------------------

function ReviewRow({ field, fieldReview, onSetFieldReview, statusTestId }) {
  return (
    <div className="rapid-inline-actions">
      {REVIEW_STATUSES.map((status) => (
        <button
          key={status}
          type="button"
          className={`btn ${fieldReview[field] === status ? 'primary' : 'ghost'} sm`}
          data-testid={`field-review-${field}-${reviewSlug(status)}`}
          onClick={() => onSetFieldReview(field, status)}
        >
          {status}
        </button>
      ))}
      {statusTestId ? (
        <span className="chip outline sm" data-testid={statusTestId}>
          {fieldReview[field] || 'Accepted'}
        </span>
      ) : (
        <span className="chip outline sm">{fieldReview[field] || 'Accepted'}</span>
      )}
    </div>
  );
}

export function ExtractionReviewView({
  fields,
  fieldReview,
  onUpdateField,
  onSetFieldReview,
  onSetExtractionStatus,
}) {
  return (
    <section className="rapid-panel" data-testid="intake-review-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Extraction review</div>
          <h3>Title, problem statement, and North Star</h3>
        </div>
      </div>
      <label className="rapid-field">
        <span>Title</span>
        <input
          value={fields.title || ''}
          onChange={(event) => onUpdateField({ title: event.target.value })}
        />
      </label>
      <ReviewRow
        field="title"
        fieldReview={fieldReview}
        onSetFieldReview={onSetFieldReview}
        statusTestId="field-review-title-status"
      />
      <label className="rapid-field">
        <span>Problem statement</span>
        <textarea
          value={fields.problemStatement || ''}
          onChange={(event) => onUpdateField({ problemStatement: event.target.value })}
          rows={5}
        />
      </label>
      <ReviewRow
        field="problemStatement"
        fieldReview={fieldReview}
        onSetFieldReview={onSetFieldReview}
      />
      <label className="rapid-field">
        <span>North Star</span>
        <textarea
          value={fields.northStar || ''}
          onChange={(event) => onUpdateField({ northStar: event.target.value })}
          rows={4}
        />
      </label>
      <ReviewRow
        field="northStar"
        fieldReview={fieldReview}
        onSetFieldReview={onSetFieldReview}
        statusTestId="field-review-northStar-status"
      />
      <div className="rapid-chip-row">
        {(fields.candidatePods || []).map((pod) => (
          <span className="chip outline sm" key={pod.pod}>
            {pod.pod}
          </span>
        ))}
      </div>
      <div className="rapid-inline-actions">
        <button
          type="button"
          className="btn primary sm"
          onClick={() => onSetExtractionStatus('Accepted')}
        >
          Accept extraction
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => onSetExtractionStatus('Needs edit')}
        >
          Mark for edit
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => onSetExtractionStatus('Rejected')}
        >
          Reject extraction
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// RFIs & Stakeholders
// ---------------------------------------------------------------------------

export function RfisStakeholdersView({ fields, rfis }) {
  return (
    <section className="rapid-panel" data-testid="intake-rfis-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Open questions</div>
          <h3>RFIs and stakeholders</h3>
        </div>
      </div>
      <div className="rapid-grid two-up">
        <section>
          <div className="eyebrow">RFIs</div>
          <ul className="rapid-record-list">
            {rfis.map((rfi) => (
              <li key={rfi.id}>
                <strong>{rfi.id}</strong>
                <p>{rfi.q || rfi.question}</p>
                <div className="muted small">To: {rfi.to || rfi.ownerLabel || 'Data SME'}</div>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <div className="eyebrow">Stakeholders</div>
          <ul className="rapid-inline-list">
            {Object.entries(fields.stakeholders || {}).map(([label, value]) => (
              <li key={label}>
                <strong>{label}:</strong> {value}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Source Transcript
// ---------------------------------------------------------------------------

export function SourceTranscriptView({ transcript }) {
  return (
    <section className="rapid-panel" data-testid="intake-transcript-view">
      <div className="eyebrow">Source transcript</div>
      <pre aria-label="Source transcript" className="rapid-transcript" tabIndex={0}>
        {transcript}
      </pre>
    </section>
  );
}
